/**
 * resultSentence — reduce a run's result to ONE human sentence for the
 * run-record page's Result section (26742). Never JSON, never a key/value
 * dump — that detail already lives in the Evidence section.
 *
 * Preference order:
 *   1. the substrate's own `summary` (from `get_run_decisions`) — written
 *      with the whole run in view, so it wins whenever present;
 *   2. the first sentence of a string `result.artifact` (the pre-formatted
 *      markdown-ish summary some workflows persist directly);
 *   3. a status-keyed fallback sentence — always non-empty, so the section
 *      never renders blank.
 */
import type { WorkflowResultBody } from "../result.types";
import { statusDisplayLabel } from "../statusLabels";

export interface ResultSentenceInput {
  /** `get_run_decisions().summary` — `null`/absent when the tool hasn't run. */
  summary?: string | null;
  status?: string | null;
  result?: WorkflowResultBody | null;
}

const MAX_LENGTH = 240;

/** Status values with a fallback sentence written for THAT status rather
 *  than the generic "This run is {label}." template — non-terminal states
 *  where "is running" reads oddly, or terminal ones that deserve their own
 *  phrasing. */
const FALLBACK_BY_STATUS: Record<string, string> = {
  completed: "This run completed.",
  failed: "This run ended in an error.",
  rejected: "This run was rejected.",
  cancelled: "This run was cancelled.",
  timeout_cancelled: "This run timed out.",
  running: "This run is still in progress.",
  paused_hitl: "This run is waiting on a response.",
  warm_hitl: "This run is waiting on a response.",
  rpa_review: "This run is waiting on a response.",
  pending_email_send: "This run is waiting for a reply to be sent.",
  pending_edocs: "This run is waiting on the carrier's documents.",
};

function truncate(text: string): string {
  return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH - 1)}…` : text;
}

/** The first sentence (or first line, absent terminal punctuation) of a
 *  pre-formatted `result.artifact` string. */
function firstSentenceOf(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  const firstLine = trimmed.split(/\r?\n/)[0].trim();
  if (firstLine.length === 0) return null;
  const [firstSentence] = firstLine.split(/(?<=[.!?])\s/);
  const candidate = (firstSentence || firstLine).trim();
  return candidate.length > 0 ? truncate(candidate) : null;
}

export function resultSentence({
  summary,
  status,
  result,
}: ResultSentenceInput): string {
  const trimmedSummary = summary?.trim();
  if (trimmedSummary) return trimmedSummary;

  if (result && typeof result.artifact === "string") {
    const fromArtifact = firstSentenceOf(result.artifact);
    if (fromArtifact) return fromArtifact;
  }

  if (status) {
    const key = status.toLowerCase();
    if (FALLBACK_BY_STATUS[key]) return FALLBACK_BY_STATUS[key];
    return `This run is ${statusDisplayLabel(status).toLowerCase()}.`;
  }

  return "No summary is available for this run.";
}

export default resultSentence;
